"use client";

import {
  Check,
  Loader2,
  Lock,
  Minus,
  Package,
  Play,
  Plus,
  ShoppingCart,
  X,
} from "lucide-react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { addShopCartLine, type ShopItemForBuyer } from "@/actions/shop";
import { PricingDisclosure } from "@/components/pricing/pricing-breakdown";
import { SendInChatButton } from "@/components/share/send-in-chat-button";
import { AppImage as Image } from "@/components/ui/app-image";
import { AvailabilityRibbon } from "@/components/ui/availability-ribbon";
import { VideoMedia } from "@/components/ui/video-media";
import { CheckoutSummary } from "@/features/shop/checkout/checkout-summary";
import { useHrefWithFrom } from "@/hooks/use-current-href";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";
import { availabilityFor } from "@/lib/availability";
import { mediaPreviewUrlFromAsset, toRenderableMedia } from "@/lib/media";
import { blurHashToDataUrl } from "@/lib/media/blurhash";
import { cn } from "@/lib/utils";
import { RequirementProgressRow } from "./requirement-progress";
import { formatMoney, maxBuyableQuantity } from "./shop-format";
import { ShopReviewsSection } from "./shop-reviews-section";

const springTransition = {
  type: "spring" as const,
  stiffness: 400,
  damping: 35,
};

export function ShopItemSheet({
  item,
  onClose,
  onAddedToCart,
}: {
  item: ShopItemForBuyer;
  onClose: () => void;
  /** Fires with the quantity added so a parent can bump its cart badge. */
  onAddedToCart?: (quantity: number) => void;
}) {
  const router = useRouter();
  useEffect(() => {
    captureClientEvent(ANALYTICS_EVENTS.SHOP_PRODUCT_VIEWED, {
      shop_item_id: item.id,
    });
  }, [item.id]);
  const cartHref = useHrefWithFrom("/shop/cart");
  const buyNowHref = useHrefWithFrom(`/shop/buy-now/${item.id}`);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isLocked = !item.isEligible;
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    () =>
      item.variants.find((variant) => !variant.soldOut)?.id ??
      item.variants[0]?.id ??
      null,
  );
  const selectedVariant =
    item.variants.find((variant) => variant.id === selectedVariantId) ?? null;
  const effectivePriceMinor =
    selectedVariant?.advertisedPriceMinor ??
    selectedVariant?.priceMinor ??
    item.advertisedPriceMinor ??
    item.priceMinor;
  const effectiveCompareAtPriceMinor =
    selectedVariant?.compareAtPriceMinor ?? item.compareAtPriceMinor;
  const effectiveSoldOut = selectedVariant?.soldOut ?? item.soldOut;
  const maxQty =
    selectedVariant?.remainingStock == null
      ? maxBuyableQuantity(item)
      : Math.min(maxBuyableQuantity(item), selectedVariant.remainingStock);
  const hasCompareAt =
    effectiveCompareAtPriceMinor !== null &&
    effectiveCompareAtPriceMinor > effectivePriceMinor;

  const [quantity, setQuantity] = useState(1);
  const [activeMediaId, setActiveMediaId] = useState(
    () => item.images[0]?.id ?? null,
  );
  const [addingToCart, setAddingToCart] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const status = buyingNow ? "processing" : "idle";

  const addToCart = async () => {
    setAddingToCart(true);
    const result = await addShopCartLine({
      shopItemId: item.id,
      shopItemVariantId: selectedVariantId,
      quantity,
    });
    setAddingToCart(false);
    if (result.ok) {
      onAddedToCart?.(quantity);
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1600);
      toast.success("Added to cart", {
        action: {
          label: "View cart",
          onClick: () => router.push(cartHref),
        },
      });
    } else {
      toast.error(result.message);
    }
  };

  // ESC closes unless the Buy Now review is opening.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && status !== "processing") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, status]);

  const subtotalMinor = effectivePriceMinor * quantity;
  const totalMinor = subtotalMinor;

  const canBuy =
    !isLocked && !effectiveSoldOut && maxQty > 0 && status !== "processing";

  const handleBuy = useCallback(() => {
    setBuyingNow(true);
    const url = new URL(buyNowHref, "https://hiive.local");
    if (selectedVariantId !== null) {
      url.searchParams.set("variantId", String(selectedVariantId));
    }
    url.searchParams.set("quantity", String(quantity));
    router.push(`${url.pathname}${url.search}`);
  }, [buyNowHref, quantity, router, selectedVariantId]);

  if (!mounted) return null;

  const activeAsset =
    item.images.find((asset) => asset.id === activeMediaId) ?? item.images[0];
  const activeMedia = activeAsset ? toRenderableMedia(activeAsset) : null;
  const src = activeMedia?.url ?? null;
  const blur = blurHashToDataUrl(activeAsset?.blurHash);
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-end sm:justify-center">
      <motion.div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={() => status !== "processing" && onClose()}
      />

      <motion.div
        className="relative z-10 flex max-h-[92%] w-full max-w-md flex-col overflow-hidden bg-background shadow-2xl sm:rounded-3xl"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={springTransition}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-3">
          <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-text-primary">
            {item.name}
          </h2>
          <div className="ml-3 flex shrink-0 items-center gap-2">
            <SendInChatButton
              entityType="shop_item"
              entityId={String(item.id)}
              aria-label="Share product"
              nativeShare={{
                title: item.name,
                text: item.description,
                url:
                  typeof window !== "undefined"
                    ? `${window.location.origin}/shop/items/${item.id}`
                    : `/shop/items/${item.id}`,
              }}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-alt text-text-secondary transition-colors hover:bg-surface-elevated"
            />
            <button
              type="button"
              aria-label="Close product"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-alt text-text-secondary transition-colors hover:bg-surface-elevated"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {/* Media */}
          <div
            className={cn(
              "relative w-full overflow-hidden rounded-2xl bg-surface-alt",
              ASPECT_RATIO_CLASS.portrait4By5,
            )}
          >
            <AvailabilityRibbon
              availability={
                selectedVariant?.availability ??
                item.availability ??
                availabilityFor({
                  remaining:
                    selectedVariant?.remainingStock ?? item.remainingStock,
                  soldOut: effectiveSoldOut,
                  unit: "item",
                })
              }
            />
            {src && activeMedia?.kind === "video" ? (
              <VideoMedia
                key={activeMedia.id}
                src={src}
                mimeType={activeMedia.mimeType}
                posterUrl={activeMedia.poster?.url}
                controls
                playsInline
                className={cn(
                  "h-full w-full bg-black object-contain",
                  (isLocked || effectiveSoldOut) && "opacity-60",
                )}
              />
            ) : src ? (
              <Image
                src={src}
                alt={item.name}
                fill
                sizes="(max-width: 640px) 90vw, 400px"
                className={cn(
                  "object-cover",
                  (isLocked || effectiveSoldOut) && "opacity-60",
                )}
                placeholder={blur ? "blur" : undefined}
                blurDataURL={blur}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-text-tertiary">
                <Package className="h-10 w-10" />
              </div>
            )}
            {isLocked && (
              <div className="absolute inset-0 flex items-center justify-center bg-surface/40 backdrop-blur-[1px]">
                <span className="flex items-center gap-1.5 rounded-full bg-surface-elevated px-3 py-1.5 text-xs font-semibold text-text-secondary shadow-sm">
                  <Lock className="h-3.5 w-3.5" /> Locked
                </span>
              </div>
            )}
          </div>

          {/* Thumbnails */}
          {item.images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {item.images.map((asset) => {
                const media = toRenderableMedia(asset);
                const thumb = mediaPreviewUrlFromAsset(asset);
                if (!thumb) return null;
                return (
                  <button
                    type="button"
                    key={asset.id}
                    aria-label={`Show media ${asset.order + 1}`}
                    onClick={() => setActiveMediaId(asset.id)}
                    className={cn(
                      "relative h-16 w-[3.2rem] shrink-0 overflow-hidden rounded-lg border bg-surface-alt",
                      activeAsset?.id === asset.id
                        ? "border-brand ring-2 ring-brand/20"
                        : "border-transparent",
                    )}
                  >
                    <Image
                      src={thumb}
                      alt={item.name}
                      fill
                      sizes="56px"
                      className="object-cover"
                      placeholder={
                        blurHashToDataUrl(asset.blurHash) ? "blur" : undefined
                      }
                      blurDataURL={blurHashToDataUrl(asset.blurHash)}
                    />
                    {media?.kind === "video" ? (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
                        <Play className="h-4 w-4 fill-current" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          {/* Price */}
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-text-primary tabular-nums">
              {formatMoney(effectivePriceMinor, item.currency)}
              <PricingDisclosure policy={item.pricingPolicy} />
            </span>
            {hasCompareAt && (
              <span className="text-base text-text-tertiary tabular-nums line-through">
                {formatMoney(
                  effectiveCompareAtPriceMinor as number,
                  item.currency,
                )}
              </span>
            )}
          </div>

          {/* Description */}
          {item.activities.length > 0 ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {item.activities.map((activity) => (
                <span
                  key={activity.id}
                  className="rounded-full bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand"
                >
                  {activity.name}
                </span>
              ))}
            </div>
          ) : null}

          {item.description && (
            <p className="mt-3 text-sm leading-relaxed whitespace-pre-line text-text-secondary">
              {item.description}
            </p>
          )}

          <ShopReviewsSection shopItemId={item.id} />

          {item.variants.length > 0 ? (
            <div className="mt-5">
              <p className="mb-2 text-sm font-semibold text-text-primary">
                Variant
              </p>
              <div className="flex flex-wrap gap-2">
                {item.variants.map((variant) => (
                  <button
                    key={variant.id}
                    type="button"
                    disabled={variant.soldOut}
                    onClick={() => {
                      setSelectedVariantId(variant.id);
                      setQuantity(1);
                    }}
                    className={cn(
                      "rounded-md border px-3 py-2 text-sm",
                      selectedVariantId === variant.id
                        ? "border-brand bg-brand/10 text-brand"
                        : "border-border text-text-secondary",
                      variant.soldOut && "cursor-not-allowed opacity-45",
                    )}
                  >
                    {variant.title}
                    {variant.soldOut ? " · Sold out" : ""}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Locked → show requirements + progress, never a buy path */}
          {isLocked ? (
            <div className="mt-5 rounded-2xl border border-border bg-surface/60 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-text-tertiary" />
                <h3 className="text-sm font-semibold text-text-primary">
                  Unlock this item
                </h3>
              </div>
              <p className="mb-3 text-sm leading-relaxed text-text-secondary">
                {item.requirementDescription}
              </p>
              {item.requirementProgress.length > 0 && (
                <div className="space-y-2">
                  {item.requirementProgress.map((requirement) => (
                    <RequirementProgressRow
                      key={requirement.label}
                      requirement={requirement}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Quantity */}
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm font-semibold text-text-primary">
                  Quantity
                </span>
                {effectiveSoldOut ? (
                  <span className="text-sm font-semibold text-error">
                    Sold out
                  </span>
                ) : maxQty === 0 ? (
                  <span className="text-sm text-text-secondary">
                    Limit reached
                  </span>
                ) : (
                  <div className="flex items-center gap-3">
                    <StepperButton
                      disabled={quantity <= 1}
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      label="Decrease quantity"
                    >
                      <Minus className="h-4 w-4" />
                    </StepperButton>
                    <span className="w-6 text-center font-semibold text-text-primary tabular-nums">
                      {quantity}
                    </span>
                    <StepperButton
                      disabled={quantity >= maxQty}
                      onClick={() =>
                        setQuantity((q) => Math.min(maxQty, q + 1))
                      }
                      label="Increase quantity"
                    >
                      <Plus className="h-4 w-4" />
                    </StepperButton>
                  </div>
                )}
              </div>
              {!effectiveSoldOut && maxQty > 0 && maxQty < 10 && (
                <p className="mt-1 text-right text-xs text-text-tertiary">
                  Up to {maxQty} per order
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer / buy button */}
        {!isLocked && !effectiveSoldOut && maxQty > 0 && (
          <div className="border-t border-border bg-background px-5 pt-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <CheckoutSummary
              totalLabel={item.pricingPolicy ? "Advertised subtotal" : "Total"}
              className="mb-3"
              subtotalMinor={subtotalMinor}
              taxIncludedMinor={0}
              taxAddedMinor={0}
              deliveryFeeMinor={0}
              totalMinor={totalMinor}
              currency={item.currency}
              showDelivery={false}
            />
            <div className="flex gap-2">
              <button
                type="button"
                disabled={addingToCart || status === "processing"}
                onClick={() => void addToCart()}
                className={cn(
                  "flex shrink-0 items-center justify-center gap-1.5 rounded-full border px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-40",
                  justAdded
                    ? "border-brand/40 bg-brand/10 text-brand"
                    : "border-border text-text-primary hover:bg-surface-alt",
                )}
              >
                {justAdded ? (
                  <>
                    <Check className="size-4" />
                    Added
                  </>
                ) : (
                  <>
                    <ShoppingCart className="size-4" />
                    Add to cart
                  </>
                )}
              </button>
              <button
                type="button"
                disabled={!canBuy}
                onClick={handleBuy}
                className={cn(
                  "flex min-w-0 flex-1 items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-all active:scale-[0.99]",
                  canBuy
                    ? "bg-brand text-white shadow-md shadow-brand/20 hover:bg-brand/90"
                    : "cursor-not-allowed bg-surface-alt text-text-tertiary",
                )}
              >
                {status === "processing" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Opening…
                  </>
                ) : (
                  <>Buy now · {formatMoney(totalMinor, item.currency)}</>
                )}
              </button>
            </div>
            <p className="mt-2 text-center text-xs text-text-tertiary">
              <PricingDisclosure policy={item.pricingPolicy} />
              Opens a dedicated review before payment. Your cart stays
              unchanged.
            </p>
          </div>
        )}
      </motion.div>
    </div>,
    document.body,
  );
}

function StepperButton({
  children,
  disabled,
  onClick,
  label,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full border border-border transition-colors",
        disabled
          ? "cursor-not-allowed text-text-tertiary opacity-50"
          : "bg-surface-elevated text-text-primary hover:bg-surface-alt",
      )}
    >
      {children}
    </button>
  );
}
